/*
  Automatic gift synchronization - The Collector, unlocked at N+ watches
  (config: snippets/aurelior-gift-config.liquid). Generalised from
  kensington-gift-sync.js, which is kept unchanged for rollback.

  Cart state comes from sections/aurelior-gift-state.liquid: Liquid
  classifies watch units by collection membership (the same rule the drawer,
  rewards bar and Shopify's 15/20/30% discounts use), so no product-ID list
  is shipped and nothing can be missed by pagination.

  Keeps exactly ONE gift while the cart holds >= minWatches watch units and
  none otherwise, and removes Kensington lines the old feature auto-added.
  Never ADDS the gift unless its current variant price is exactly 0 and it is
  available. Client-side convenience only - not protection against direct
  Cart API use.

  window.aureliorGiftSync.settled() resolves with the result of the newest
  run ({ ok, watchQty, confirmed, unlockedAt }) for the add-to-cart toast.

  While a run is in flight the card keeps its locked look (see
  snippets/cart-collector-gift.liquid). If a run ends without the gift it is
  owed, html.aurelior-gift-failed reveals a "try again" link on that card; the
  class sits on <html> so it survives drawer re-renders.
*/
(function () {
  var cfg = window.aureliorGift;
  if (!cfg || !cfg.variantId || !window.routes) return;

  var SOURCE = 'aurelior-gift-sync';
  var MAX_PASSES = 3;
  var chain = Promise.resolve();
  var queued = false;

  var api = (window.aureliorGiftSync = {
    last: null,
    // Waits one task so a cart-update published right after the caller (e.g.
    // the PDP cross-sell shows its toast before publishing) is included.
    settled: function () {
      return new Promise(function (resolve) { setTimeout(resolve, 0); })
        .then(function () { return chain; })
        .then(function () { return api.last; });
    }
  });

  function readState(html) {
    if (!html) return null;
    var el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-aurelior-gift-state]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function fetchState() {
    return fetch(window.routes.cart_url + '?section_id=' + cfg.stateSection, { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('gift state ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var s = readState(html);
        if (!s) throw new Error('gift state unreadable');
        return s;
      });
  }

  function post(url, body) {
    body.sections = ['cart-drawer', 'cart-icon-bubble', cfg.stateSection].join(',');
    // Render sections in the visitor's locale (/en/...), not the primary one.
    body.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  function render(response, state) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !response || !response.sections || typeof drawer.renderContents !== 'function') return;
    // shouldOpen = false: syncing must never pop the drawer open.
    drawer.renderContents({ id: cfg.variantId, sections: response.sections }, false);
    // renderContents never re-applies the empty state; do it when the cart
    // just became empty (gift removed with the last paid item).
    if (state) drawer.classList.toggle('is-empty', state.itemCount === 0);
  }

  // The one correction this state needs, or null when it is already right.
  function plan(s) {
    var free = s.gift.priceCents === 0;
    var want = free && s.watchQty >= cfg.minWatches ? 1 : 0;
    var updates = {};
    var n = 0;
    s.legacyLines.forEach(function (l) { updates[l.key] = 0; n++; });
    // While the gift is free every line of it is ours to manage; if it ever
    // gets a real price, only lines this feature added are removed and a
    // paid line of the same product is never touched.
    var lines = s.giftLines.filter(function (l) { return free || l.auto; });
    lines.forEach(function (l, i) {
      var q = i === 0 ? want : 0;
      if (l.qty !== q) { updates[l.key] = q; n++; }
    });
    if (n) return { updates: updates };
    if (want === 1 && lines.length === 0 && s.gift.available) return { add: true };
    return null;
  }

  function confirmedIn(s) {
    var lines = s.giftLines;
    return s.watchQty >= cfg.minWatches && lines.length === 1 && lines[0].qty === 1 && lines[0].final === 0;
  }

  function setFailed(failed) {
    document.documentElement.classList.toggle('aurelior-gift-failed', failed);
  }

  function run() {
    var passes = 0;
    var added = false;
    var lastResponse = null;
    setFailed(false);

    function step(state) {
      var p = plan(state);
      if (!p || passes >= MAX_PASSES) return state;
      passes++;
      var request = p.add
        ? post(window.routes.cart_add_url + '.js', {
            items: [{ id: cfg.variantId, quantity: 1, properties: { _auto_gift: cfg.key } }]
          })
        : post(window.routes.cart_update_url + '.js', { updates: p.updates });
      return request.then(function (response) {
        if (p.add) added = true;
        lastResponse = response;
        var next = readState(response.sections && response.sections[cfg.stateSection]);
        render(response, next);
        return next ? step(next) : fetchState().then(step);
      });
    }

    return fetchState()
      .then(step)
      .then(function (state) {
        var confirmed = confirmedIn(state);
        var prev = api.last;
        setFailed(!confirmed && state.watchQty >= cfg.minWatches && state.gift.priceCents === 0 && state.gift.available);
        api.last = {
          ok: true,
          watchQty: state.watchQty,
          confirmed: confirmed,
          // When the gift was (re)added: lets the toast announce it only for
          // the add that actually unlocked it.
          unlockedAt: confirmed ? (added ? Date.now() : (prev && prev.confirmed && prev.unlockedAt) || 0) : 0
        };
        if (lastResponse && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: lastResponse, gift: api.last });
        }
      })
      .catch(function () {
        // Unknown state: never claim the gift; the next cart change or the
        // card's "try again" link retries.
        setFailed(true);
        api.last = { ok: false, watchQty: null, confirmed: false, unlockedAt: 0 };
      });
  }

  // Serialized + coalesced: at most one run in flight and one queued; each run
  // re-reads the cart and makes at most MAX_PASSES corrections (no loops).
  function sync() {
    if (queued) return;
    queued = true;
    chain = chain.then(function () {
      queued = false;
      return run();
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
      if (evt && evt.source === SOURCE) return;
      sync();
    });
  }
  document.addEventListener('click', function (evt) {
    if (evt.target.closest && evt.target.closest('[data-aurelior-gift-retry]')) sync();
  });
  // Back/forward cache restore (e.g. returning from checkout).
  window.addEventListener('pageshow', function (evt) { if (evt.persisted) sync(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
})();
